import { CLEVERTAP_ACCOUNT_ID, ENVIRONMENT } from '@/constants/env'

import { getDeviceId, getDeviceModel } from './clarity.utils'

const ENABLED_ENVIRONMENTS = ['staging', 'preprod', 'production']

// What the Android and iOS apps send when the store's home opens. The Web SDK
// has no "App Launched" (a mobile-only system event), and its own "Web Session
// Started" does not reach this project, so the web sends the mobile one.
export const STORE_HOME_LAUNCHED = 'Store Home Launched'
const HOME_LAUNCHED_SESSION_KEY = 'ctStoreHomeLaunched'

type CleverTap = typeof import('clevertap-web-sdk').default

// Resolves to the initialised SDK, or null when it failed to load.
let sdk: Promise<CleverTap | null> | null = null

export interface CleverTapStoreProfile {
  id?: string | null
  ownerName?: string | null
  email?: string | null
  phoneCode?: string | null
  phoneNumber?: string | null
  address?: string | null
  storeName?: string | null
  storeType?: string | null
  location?: { lat?: unknown; lng?: unknown } | null
  city?: string | null
  region?: string | null
}

/**
 * The SDK's getLocation(lat, lng) silently falls back to asking the browser
 * for the user's own position (a permission prompt) when either value is
 * falsy, and rejects the bounds themselves. Only a position it will take as
 * given is passed on.
 */
function isUsableLocation(
  location: CleverTapStoreProfile['location']
): location is { lat: number; lng: number } {
  const { lat, lng } = location ?? {}
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    lat !== 0 &&
    lng !== 0 &&
    lat > -90 &&
    lat <= 90 &&
    lng > -180 &&
    lng <= 180
  )
}

/**
 * The project is chosen entirely by the build's VITE_CLEVERTAP_ACCOUNT_ID
 * (a per-environment GitHub variable). It stays off locally and wherever a
 * build is not given an id, e.g. white-label.
 */
export function initCleverTap(): void {
  if (
    !CLEVERTAP_ACCOUNT_ID ||
    !ENABLED_ENVIRONMENTS.includes(ENVIRONMENT) ||
    sdk ||
    typeof window === 'undefined'
  )
    return

  // Lazy: the SDK is ~100 kB gzipped and nothing needs it before first paint.
  sdk = import('clevertap-web-sdk')
    .then(({ default: clevertap }) => {
      clevertap.privacy.push({ optOut: false })
      // Lets CleverTap fill the profile's Country (City when it can resolve
      // one) from the visitor's IP, as the mobile SDKs already do. That is
      // where the browser is, not the store: the store's own place stays in
      // user_city, user_region and address.
      clevertap.privacy.push({ useIP: true })
      clevertap.spa = true
      // EU1 is the SDK's default region, which is where both projects live.
      clevertap.init(CLEVERTAP_ACCOUNT_ID)
      return clevertap
    })
    .catch(error => {
      console.warn('CleverTap failed to load', error)
      return null
    })
}

/**
 * Same keys the Android/iOS store apps send, so one store is one profile
 * across platforms. No `language`: the web app is pinned to 'en' and would
 * overwrite the value the mobile apps report.
 */
export function buildCleverTapProfile(
  store: CleverTapStoreProfile,
  environment: string | undefined = ENVIRONMENT
): Record<string, string> | null {
  if (!store.id) return null

  const profile: Record<string, string | undefined> = {
    Identity: store.id,
    Name: store.ownerName ?? undefined,
    Email: store.email ?? undefined,
    storeId: store.id,
    phone:
      store.phoneCode && store.phoneNumber
        ? `${store.phoneCode}${store.phoneNumber}`
        : undefined,
    address: store.address ?? undefined,
    'Store name': store.storeName ?? undefined,
    'Store type': store.storeType ?? undefined,
    user_city: store.city ?? undefined,
    user_region: store.region ?? undefined,
    // Staging and preprod share the TEST project: this tells them apart.
    Environment: environment
  }

  return Object.fromEntries(
    Object.entries(profile).filter(([, value]) => value)
  ) as Record<string, string>
}

/**
 * Identify the signed-in store. The SDK switches profiles on a new Identity
 * and just updates the attributes on a repeat one, so this is safe to call on
 * every login and every app open.
 */
export function identifyCleverTapUser(store: CleverTapStoreProfile): void {
  const Site = buildCleverTapProfile(store)
  if (!sdk || !Site) return

  const { location } = store

  // Chained on the init promise, so identify can never run before init. The
  // position is set after the login push, so it lands on the store's profile.
  void sdk.then(clevertap => {
    clevertap?.onUserLogin.push({ Site })
    if (isUsableLocation(location))
      clevertap?.getLocation(location.lat, location.lng)
  })
}

/**
 * Report that the store's home was opened, once per browser session and
 * store (the mobile apps send it on every launch). Call it after identify so
 * the event belongs to the store's profile.
 */
export function trackStoreHomeLaunched(
  store: Pick<CleverTapStoreProfile, 'id' | 'storeType'>
): void {
  if (!sdk || !store.id) return

  try {
    if (sessionStorage.getItem(HOME_LAUNCHED_SESSION_KEY) === store.id) return
    sessionStorage.setItem(HOME_LAUNCHED_SESSION_KEY, store.id)
  } catch {
    // Storage blocked: better a repeat than no event.
  }

  pushCleverTapEvent(STORE_HOME_LAUNCHED, {
    StoreId: store.id,
    ...(store.storeType && { StoreType: store.storeType }),
    Platform: 'Web',
    Environment: ENVIRONMENT,
    DeviceId: getDeviceId(),
    DeviceModel: getDeviceModel()
  })
}

// CleverTap event properties: scalars, or arrays of strings / numbers only
// (no nested objects).
export type CleverTapEventValue =
  | string
  | number
  | boolean
  | string[]
  | number[]

/**
 * Send a business event. A no-op wherever CleverTap is off (local dev,
 * white-label, no account id), and chained on the init promise like identify,
 * so it can never run before init. Never throws.
 */
export function pushCleverTapEvent(
  name: string,
  props?: Record<string, CleverTapEventValue>
): void {
  if (!sdk) return

  void sdk
    .then(clevertap => clevertap?.event.push(name, props))
    .catch(error => console.warn('CleverTap event failed', error))
}
